import React from "react";

import { graphql, useStaticQuery } from "gatsby";

import useLanguage from "../../hooks/useLanguage";
import { AnyObject } from "../../interfaces/anyObject";
import BrowserDetection from "../../utils/browserDetection";

interface SEOProps {
  description?: string;
  linkingData?: AnyObject[];
  title: string;
  twitterTitle?: string;
  twitterSite?: string;
  twitterImage?: Queries.Maybe<readonly Queries.Maybe<Partial<Queries.kontent_item_asset_element>>[]>;
  twitterDescription?: string;
  twitterCreator?: string;
  ogTitle?: string;
  ogImage?: Queries.Maybe<readonly Queries.Maybe<Partial<Queries.kontent_item_asset_element>>[]>;
  ogDescription?: string;
  isHiddenForRobots?: boolean;
  tag?: string;
}

const SEO: React.FC<SEOProps> = ({
  description = "",
  linkingData = null,
  title,
  twitterTitle = "",
  twitterSite = "",
  twitterImage = [],
  twitterDescription = "",
  twitterCreator = "",
  ogTitle = "",
  ogImage = [],
  ogDescription = "",
  isHiddenForRobots = false,
  tag = undefined,
}) => {
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
        }
      }

      siteSettings: kontentItemSiteSettings(system: {codename: {eq: "site_settings"}}) {
        elements {
          translate {
            value {
              codename
            }
          }
        }
      }
    }
  `);

  const { languageCode } = useLanguage();

  const metaDescription = description || data.site.siteMetadata.description;

  const classNames = BrowserDetection.hasTouchScreen() ? "touchable " : "";

  const translateable = data?.siteSettings?.elements?.translate?.value[0]?.codename !== "no";

  return (
    <>
      <html className={classNames.trim()} lang={languageCode || "en"} translate={translateable ? "yes" : "no"} />
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={(ogImage?.length ?? 0) > 0 ? `${ogImage?.[0]?.url}?h=630&w=1200` : ""} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={twitterCreator || data.site.siteMetadata.author} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:image" content={(twitterImage?.length ?? 0) > 0 ? `${twitterImage?.[0]?.url}?h=630&w=1200` : ""} />
      <meta name="twitter:title" content={twitterTitle || title} />
      <meta name="twitter:description" content={twitterDescription || metaDescription} />
      {tag && <meta name="addsearch-custom-field" content={`customTag=${tag}`} />}
      {process.env.GATSBY_GOOGLE_SITE_VERIFICATION && <meta name="google-site-verification" content={process.env.GATSBY_GOOGLE_SITE_VERIFICATION} />}

      <meta name="robots" content={isHiddenForRobots ? "noindex,nofollow" : "index,follow"} />

      {linkingData && linkingData.length > 0
        && ([] as AnyObject[]).concat(linkingData).map((item, index) => (
          <script key={`${item["@type"]}${index.toString()}`} type="application/ld+json">
            {JSON.stringify(item)}
          </script>
        ))}

      {translateable && (
        <>
          <meta name="robots" content="notranslate" />
          <meta name="googlebot" content="notranslate" />
        </>
      )}
    </>
  );
};

export default SEO;
